<template>
  <b-card no-body class="mb-2">
    <b-card-body>
      <h6 class="mb-1">Аудио из видео</h6>

      <div v-if="isLoadingVideos" class="text-center py-2">
        <b-spinner label="Loading..." />
      </div>

      <div v-else-if="!videoOptions.length" class="text-muted">
        Нет поддерживаемых видео (Кинопоиск/YouTube)
      </div>

      <div v-else>
        <b-overlay :show="isExtracting" rounded="sm">
          <template #overlay>
            <div class="text-center">
              <b-spinner label="Loading..." class="mb-50" />
              <div>Извлекаем аудио из видео… это может занять до минуты</div>
            </div>
          </template>

          <div class="d-flex flex-wrap align-items-center" style="gap: 8px">
            <b-form-select
                v-model="selectedVideoId"
                :options="selectOptions"
                :disabled="isExtracting"
                style="max-width: 400px"
            />
            <b-button
                variant="primary"
                :disabled="!selectedVideoId || isExtracting"
                @click="extractAudio"
            >
              <feather-icon icon="DownloadIcon" class="mr-50" />
              Загрузить аудио
            </b-button>
          </div>
        </b-overlay>

        <div v-if="extracted && previewUrl" class="mt-1">
          <strip-audio-cutter
              v-model="audioRegion"
              :audio-url="previewUrl"
              :default-start="0"
              :default-duration="DEFAULT_DURATION_SECONDS"
          />

          <b-form-group label="Название" class="mt-1 mb-50">
            <b-form-input v-model="title" maxlength="255" />
          </b-form-group>

          <b-button
              variant="success"
              :disabled="!canSave || isSaving"
              @click="save"
          >
            <b-spinner v-if="isSaving" small class="mr-50" />
            <feather-icon v-else icon="SaveIcon" class="mr-50" />
            Сохранить в трейлеры
          </b-button>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardBody,
  BSpinner,
  BFormSelect,
  BButton,
  BOverlay,
  BFormGroup,
  BFormInput,
} from 'bootstrap-vue'
import store from '@/store'
import { ref, computed, onMounted, onBeforeUnmount } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import StripAudioCutter from '@/views/strip/builder/StripAudioCutter.vue'

const SUPPORTED_SITES = ['KINOPOISK_WIDGET', 'YOUTUBE']
const SITE_LABELS = { KINOPOISK_WIDGET: 'Кинопоиск', YOUTUBE: 'YouTube' }
const DEFAULT_DURATION_SECONDS = 40

export default {
  name: 'TrailerAudioExtractor',
  components: {
    BCard,
    BCardBody,
    BSpinner,
    BFormSelect,
    BButton,
    BOverlay,
    BFormGroup,
    BFormInput,
    StripAudioCutter,
  },
  props: {
    movieId: { type: Number, required: true },
  },
  setup(props, { emit }) {
    const toast = useToast()

    const videos = ref([])
    const isLoadingVideos = ref(true)

    const videoOptions = computed(() => videos.value
      .filter(video => SUPPORTED_SITES.includes(video.site))
      .map(video => ({
        value: video.id,
        text: `${video.name || video.url} — ${SITE_LABELS[video.site] || video.site}`,
      })))

    const selectOptions = computed(() => [
      { value: null, text: 'Выберите видео…', disabled: true },
      ...videoOptions.value,
    ])

    const selectedVideoId = ref(null)
    const selectedVideo = computed(
      () => videos.value.find(video => video.id === selectedVideoId.value) || null,
    )

    const isExtracting = ref(false)
    const extracted = ref(null) // {tmp_path, url, duration}
    // Blob-URL превью: mp3 идёт через бэкенд (напрямую с S3 браузер не отдаст — CORS),
    // а blob-URL не требует ни CORS, ни https
    const previewUrl = ref(null)
    const audioRegion = ref({ startSeconds: 0, durationSeconds: DEFAULT_DURATION_SECONDS })
    const title = ref('')
    const isSaving = ref(false)

    const releasePreviewUrl = () => {
      if (previewUrl.value) {
        URL.revokeObjectURL(previewUrl.value)
        previewUrl.value = null
      }
    }

    const canSave = computed(() => !!extracted.value
      && !!title.value.trim()
      && audioRegion.value.durationSeconds > 0)

    const fetchVideos = () => {
      isLoadingVideos.value = true
      store.dispatch('movies/fetchMovieVideos', { id: props.movieId })
        .then(response => { videos.value = response.data.data })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка при загрузке списка видео',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => { isLoadingVideos.value = false })
    }

    const extractAudio = () => {
      if (!selectedVideoId.value) return
      // Захватываем видео ДО долгого dispatch: пользователь может успеть сменить
      // выбор в селекте, пока extract ещё выполняется (20-60 сек)
      const requestedVideoId = selectedVideoId.value
      const requestedVideo = selectedVideo.value
      isExtracting.value = true
      extracted.value = null
      releasePreviewUrl()
      store.dispatch('movies/extractTrailerAudio', {
        movieId: props.movieId,
        videoId: requestedVideoId,
        site: requestedVideo && requestedVideo.site,
      })
        .then(response => store
          .dispatch('movies/fetchTrailerTmpAudio', {
            movieId: props.movieId,
            tmpPath: response.data.tmp_path,
          })
          .then(audioResponse => {
            previewUrl.value = URL.createObjectURL(audioResponse.data)
            extracted.value = response.data
            const duration = response.data.duration || DEFAULT_DURATION_SECONDS
            audioRegion.value = {
              startSeconds: 0,
              durationSeconds: Math.min(DEFAULT_DURATION_SECONDS, duration),
            }
            title.value = (requestedVideo && requestedVideo.name) || ''
          }))
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || 'Не удалось извлечь аудио из видео',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => { isExtracting.value = false })
    }

    const save = () => {
      if (!canSave.value) return
      const start = audioRegion.value.startSeconds
      const end = start + audioRegion.value.durationSeconds
      isSaving.value = true
      store.dispatch('movies/saveTrailerAudioFromVideo', {
        movieId: props.movieId,
        tmpPath: extracted.value.tmp_path,
        start,
        end,
        title: title.value.trim(),
      })
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Аудио сохранено в трейлеры',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          emit('saved')
          extracted.value = null
          releasePreviewUrl()
          title.value = ''
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || 'Не удалось сохранить аудио',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => { isSaving.value = false })
    }

    onMounted(fetchVideos)
    onBeforeUnmount(releasePreviewUrl)

    return {
      DEFAULT_DURATION_SECONDS,
      isLoadingVideos,
      videoOptions,
      selectOptions,
      selectedVideoId,
      isExtracting,
      extracted,
      previewUrl,
      audioRegion,
      title,
      isSaving,
      canSave,
      extractAudio,
      save,
    }
  },
}
</script>
